import type { NextRouter } from "next/router";
import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { routerHasCustomerLoginInvitationScope, type CustomerLoginRouterScope } from "@Front/Components/Layouts/Login/helpers/customerLoginRouterScope";
import { useEmailSubmission, useSmsCodeSubmission } from "@Front/Components/Layouts/Login/hooks/helpers/loginSubmissionHooks";
import type { CustomerLoginState } from "@Front/Components/Layouts/Login/hooks/helpers/loginStateHook";
import type { CustomerLoginCandidateDto } from "@Front/Api/Public/emailAuthCandidatesApi";
import {
	runChooseCustomerActorChallenge,
	runSelectCustomerLoginTargetChallenge,
} from "./loginChallengesDisambiguationExecution";
import { mailVerifyScopeFromLoginState, withDisambiguationBusy } from "./loginDisambiguationScopeHelpers";
import { useCustomerOfficeSelectHandler } from "./useCustomerOfficeSelectHandler";

export type LoginChallengesHandlersParams = {
	router: NextRouter;
	loginScope: CustomerLoginRouterScope;
	loginState: CustomerLoginState;
	refresh: () => Promise<void>;
};

type LoginFormSubmitFn = (e: FormEvent<HTMLFormElement> | null, values: Record<string, string>) => Promise<void>;

export type LoginChallengesHandlersResult = {
	onEmailFormSubmit: LoginFormSubmitFn;
	onSmsCodeSubmit: LoginFormSubmitFn;
	onChooseCustomerActor: () => Promise<void>;
	onChooseThirdPartyActor: () => void;
	onSelectCustomerOffice: (officeUid: string) => Promise<void>;
	onSelectCustomerLoginTarget: (row: CustomerLoginCandidateDto) => Promise<void>;
	disambiguationBusy: boolean;
};

function useCustomerLoginEmailSubmit(params: LoginChallengesHandlersParams): LoginFormSubmitFn {
	const { router, loginScope, loginState } = params;
	const {
		setEmail,
		setPartialPhoneNumber,
		setDeliveryChannel,
		setMaskedEmail,
		setTotpCodeUid,
		setStep,
		setValidationErrors,
		setLoginCandidates,
		setAuthDisambiguation,
		setCustomerTargetChoiceCandidates,
	} = loginState;

	return useEmailSubmission({
		router,
		...loginScope,
		invitationScopeFromRouter: routerHasCustomerLoginInvitationScope(loginScope),
		setEmail,
		setPartialPhoneNumber,
		setDeliveryChannel,
		setMaskedEmail,
		setTotpCodeUid,
		setStep,
		setValidationErrors,
		setLoginCandidates,
		setAuthDisambiguation,
		setCustomerTargetChoiceCandidates,
	});
}

function useCustomerLoginSmsSubmit(params: LoginChallengesHandlersParams): LoginFormSubmitFn {
	const { router, loginScope, loginState, refresh } = params;
	const { email, authDisambiguation, setStep, setTotpCode, setValidationErrors } = loginState;

	return useSmsCodeSubmission({
		email,
		router,
		...loginScope,
		authDisambiguation,
		setStep,
		setTotpCode,
		setValidationErrors,
		refresh,
	});
}

function useLoginChallengesEmailSms(params: LoginChallengesHandlersParams): {
	onEmailFormSubmit: LoginFormSubmitFn;
	onSmsCodeSubmit: LoginFormSubmitFn;
} {
	return {
		onEmailFormSubmit: useCustomerLoginEmailSubmit(params),
		onSmsCodeSubmit: useCustomerLoginSmsSubmit(params),
	};
}

function useCustomerActorChoiceHandler(
	loginScope: CustomerLoginRouterScope,
	loginState: CustomerLoginState,
	setDisambiguationBusy: (v: boolean) => void,
): () => Promise<void> {
	const { loginCandidates, email, setAuthDisambiguation, setStep, setValidationErrors, setCustomerTargetChoiceCandidates } = loginState;

	return useCallback(async () => {
		const snap = loginCandidates;
		if (!snap || !email) {
			return;
		}
		await withDisambiguationBusy(setDisambiguationBusy, async () => {
			const scope = mailVerifyScopeFromLoginState(loginScope, loginState);
			await runChooseCustomerActorChallenge({
				snap,
				email,
				scope,
				setAuthDisambiguation,
				setStep,
				setValidationErrors,
				invitationScopeFromRouter: routerHasCustomerLoginInvitationScope(loginScope),
				setCustomerTargetChoiceCandidates,
			});
		});
	}, [
		loginCandidates,
		email,
		loginScope,
		loginState,
		setAuthDisambiguation,
		setCustomerTargetChoiceCandidates,
		setStep,
		setValidationErrors,
		setDisambiguationBusy,
	]);
}

function useThirdPartyActorHandler(email: string, router: NextRouter): () => void {
	return useCallback(() => {
		if (!email) {
			return;
		}
		router.push(`/third-party/login?email=${encodeURIComponent(email)}`);
	}, [email, router]);
}

function useCustomerTargetSelectHandler(
	loginScope: CustomerLoginRouterScope,
	loginState: CustomerLoginState,
	setDisambiguationBusy: (v: boolean) => void,
): (row: CustomerLoginCandidateDto) => Promise<void> {
	const { email, loginCandidates, authDisambiguation, setAuthDisambiguation, setValidationErrors } = loginState;

	return useCallback(
		async (row: CustomerLoginCandidateDto) => {
			if (!email) {
				return;
			}
			await withDisambiguationBusy(setDisambiguationBusy, async () => {
				const scope = mailVerifyScopeFromLoginState(loginScope, loginState);
				await runSelectCustomerLoginTargetChallenge({
					email,
					row,
					loginCandidates,
					authDisambiguation,
					scope,
					setAuthDisambiguation,
					setValidationErrors,
				});
			});
		},
		[
			email,
			loginCandidates,
			authDisambiguation,
			loginScope,
			loginState,
			setAuthDisambiguation,
			setValidationErrors,
			setDisambiguationBusy,
		],
	);
}

export function useLoginChallengesHandlers(params: LoginChallengesHandlersParams): LoginChallengesHandlersResult {
	const { router, loginScope, loginState } = params;
	const [disambiguationBusy, setDisambiguationBusy] = useState(false);

	const { onEmailFormSubmit, onSmsCodeSubmit } = useLoginChallengesEmailSms(params);
	const onChooseCustomerActor = useCustomerActorChoiceHandler(loginScope, loginState, setDisambiguationBusy);
	const onChooseThirdPartyActor = useThirdPartyActorHandler(loginState.email, router);
	const onSelectCustomerOffice = useCustomerOfficeSelectHandler(loginScope, loginState, setDisambiguationBusy);
	const onSelectCustomerLoginTarget = useCustomerTargetSelectHandler(loginScope, loginState, setDisambiguationBusy);

	return {
		onEmailFormSubmit,
		onSmsCodeSubmit,
		onChooseCustomerActor,
		onChooseThirdPartyActor,
		onSelectCustomerOffice,
		onSelectCustomerLoginTarget,
		disambiguationBusy,
	};
}
